import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, User, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';
import VIPApprovalModal from '../../components/promoters/VIPApprovalModal';

interface VIPRequest {
  id: string;
  promoterName: string;
  promoterCompany: string;
  requestedBy: string;
  requestedDate: string;
  reason: string;
  prospectName: string;
  prospectCompany: string;
  status: 'pending' | 'approved' | 'rejected';
}

const VIPRequests: React.FC = () => {
  const { t } = useTranslation();
  const [selectedRequest, setSelectedRequest] = useState<VIPRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');

  const [requests, setRequests] = useState<VIPRequest[]>([
    {
      id: '1',
      promoterName: 'Sarah Johnson',
      promoterCompany: 'Tech Corp',
      requestedBy: 'John Smith',
      requestedDate: '2024-03-15',
      reason: 'High-value prospect in the same industry. Sarah has excellent knowledge of our enterprise features.',
      prospectName: 'Michael Chen',
      prospectCompany: 'Innovation Labs',
      status: 'pending',
    },
    {
      id: '2',
      promoterName: 'David Martinez',
      promoterCompany: 'Cloud Solutions Inc',
      requestedBy: 'Emma Wilson',
      requestedDate: '2024-03-14',
      reason: 'Similar company size and use case. David can speak to scalability concerns.',
      prospectName: 'Lisa Anderson',
      prospectCompany: 'Digital Dynamics',
      status: 'pending',
    },
    {
      id: '3',
      promoterName: 'Robert Taylor',
      promoterCompany: 'Finance Hub',
      requestedBy: 'Alex Brown',
      requestedDate: '2024-03-13',
      reason: 'Prospect is in finance industry, Robert has deep finance expertise.',
      prospectName: 'Jennifer White',
      prospectCompany: 'Global Investments',
      status: 'approved',
    },
  ]);

  const handleApprove = (requestId: string) => {
    setRequests(prev =>
      prev.map(req =>
        req.id === requestId ? { ...req, status: 'approved' as const } : req
      )
    );
  };

  const handleReject = (requestId: string, reason: string) => {
    setRequests(prev =>
      prev.map(req =>
        req.id === requestId ? { ...req, status: 'rejected' as const } : req
      )
    );
  };

  const handleViewRequest = (request: VIPRequest) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const filteredRequests = requests.filter(req =>
    filterStatus === 'all' ? true : req.status === filterStatus
  );

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center space-x-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            <span>{t('vipRequests.status.pending')}</span>
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            <span>{t('vipRequests.status.approved')}</span>
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            <span>{t('vipRequests.status.rejected')}</span>
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Crown className="w-7 h-7 text-yellow-600" />
            <span>{t('vipRequests.heading')}</span>
          </h1>
          <p className="text-gray-600 mt-1">{t('vipRequests.subheading')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">{t('vipRequests.stats.totalRequests')}</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{requests.length}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-yellow-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-yellow-600">{t('vipRequests.stats.pending')}</h3>
            <Clock className="w-5 h-5 text-yellow-600" />
          </div>
          <p className="text-3xl font-bold text-yellow-600">{pendingCount}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-green-600">{t('vipRequests.stats.approved')}</h3>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-600">{approvedCount}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-red-600">{t('vipRequests.stats.rejected')}</h3>
            <XCircle className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-3xl font-bold text-red-600">{rejectedCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('vipRequests.filter.title')}</h2>
          </div>
          <div className="mt-4 flex items-center space-x-3">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'all'
                  ? 'bg-[#4339A0] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('vipRequests.filter.all', { count: requests.length })}
            </button>
            <button
              onClick={() => setFilterStatus('pending')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'pending'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('vipRequests.filter.pending', { count: pendingCount })}
            </button>
            <button
              onClick={() => setFilterStatus('approved')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'approved'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('vipRequests.filter.approved', { count: approvedCount })}
            </button>
            <button
              onClick={() => setFilterStatus('rejected')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'rejected'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('vipRequests.filter.rejected', { count: rejectedCount })}
            </button>
          </div>
        </div>

        <div className="p-6">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <Crown className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t(`vipRequests.empty.${filterStatus}`)}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <div
                  key={request.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{request.promoterName}</h3>
                        <p className="text-sm text-gray-600">{request.promoterCompany}</p>
                      </div>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3 pl-13">
                    <div>
                      <p className="text-xs text-gray-500">{t('vipRequests.fields.forProspect')}</p>
                      <p className="font-medium text-gray-900">{request.prospectName}</p>
                      <p className="text-sm text-gray-600">{request.prospectCompany}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('vipRequests.fields.requestedBy')}</p>
                      <p className="font-medium text-gray-900">{request.requestedBy}</p>
                      <p className="text-sm text-gray-600">{request.requestedDate}</p>
                    </div>
                  </div>

                  <div className="pl-13 mb-3">
                    <p className="text-sm text-gray-700 line-clamp-2">{request.reason}</p>
                  </div>

                  <div className="pl-13 flex items-center justify-end space-x-3">
                    <button
                      onClick={() => handleViewRequest(request)}
                      className="px-4 py-2 text-[#4339A0] bg-[#f5f3ff] rounded-lg hover:bg-[#e8e4ff] transition-colors"
                    >
                      {t('vipRequests.buttons.viewDetails')}
                    </button>
                    {request.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleReject(request.id, '')}
                          className="px-4 py-2 text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          {t('vipRequests.buttons.reject')}
                        </button>
                        <button
                          onClick={() => handleApprove(request.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          {t('vipRequests.buttons.approve')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedRequest && (
        <VIPApprovalModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          request={selectedRequest}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  );
};

export default VIPRequests;
